import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus,
  Mail,
  Phone,
  MessageCircle,
  X,
} from "lucide-react";

export const FloatingActionButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      icon: Mail,
      label: "Email Us",
      href: "mailto:info@amaqis.ai?subject=Inquiry from amasQIS Website&body=Hello amasQIS Team,%0D%0A%0D%0AI am interested in learning more about your AI solutions and services.%0D%0A%0D%0ABest regards,",
      color: "#00CFFF",
    },
    {
      icon: Phone,
      label: "Call Oman & Dubai",
      href: "tel:+917708113987",
      color: "#0FFFA0",
    },
    {
      icon: Phone,
      label: "Call India", 
      href: "tel:+919025226518",
      color: "#8A3FFC",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      href: "https://wa.me/919025226518?text=Hello%20amasQIS%20Team%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20AI%20solutions%20and%20services.%0A%0ABest%20regards%2C",
      color: "#25D366",
    },
  ];

  const handleActionClick = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile: Bottom-right positioning */}
      <div className="fixed bottom-4 right-4 z-50 lg:hidden">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className="flex flex-col gap-2 mb-3 items-end"
            >
              {actions.map((action, index) => (
                <motion.a
                  key={action.label}
                  href={action.href}
                  target={
                    action.href.startsWith("http")
                      ? "_blank"
                      : "_self"
                  }
                  rel={
                    action.href.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  onClick={handleActionClick}
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 50, opacity: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-strong rounded-full p-3 hover:scale-110 transition-all duration-300 group flex items-center gap-2 pl-4 max-w-xs"
                  style={{ borderColor: action.color }}
                  aria-label={action.label}
                >
                  <span className="text-xs sm:text-sm whitespace-nowrap transition-opacity text-white">
                    {action.label}
                  </span>
                  <action.icon
                    className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                    style={{ color: action.color }}
                  />
                </motion.a>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(!isOpen)}
          className="glass-strong rounded-full p-3 sm:p-4 neon-glow hover:bg-primary/20 transition-all duration-300"
          aria-label={isOpen ? "Close contact menu" : "Open contact menu"}
        >
          <motion.div
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isOpen ? (
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            ) : (
              <Plus className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            )}
          </motion.div>
        </motion.button>
      </div>

      {/* Desktop: Bottom-left positioning */}
      <div className="fixed bottom-6 left-6 z-50 hidden lg:block">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className="flex flex-col gap-3 mb-3"
            >
              {actions.map((action, index) => (
                <motion.a
                  key={action.label}
                  href={action.href}
                  target={
                    action.href.startsWith("http")
                      ? "_blank"
                      : "_self"
                  }
                  rel={
                    action.href.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  onClick={handleActionClick}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -50, opacity: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-strong rounded-full p-3 hover:scale-110 transition-all duration-300 group flex items-center gap-3 pr-4"
                  style={{ borderColor: action.color }}
                  aria-label={action.label}
                >
                  <action.icon
                    className="w-5 h-5"
                    style={{ color: action.color }}
                  />
                  <span className="text-sm whitespace-nowrap transition-opacity text-white">
                    {action.label}
                  </span>
                </motion.a>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(!isOpen)}
          className="glass-strong rounded-full p-4 neon-glow hover:bg-primary/20 transition-all duration-300"
          aria-label={isOpen ? "Close contact menu" : "Open contact menu"}
        >
          <motion.div
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-primary" />
            ) : (
              <Plus className="w-6 h-6 text-primary" />
            )}
          </motion.div>
        </motion.button>
      </div>
    </>
  );
};